<template>
  <div class="form-fields" v-if="renderableFormFields.length">
    <component :is="getFieldComponent(field.type)"
      v-for="field in renderableFormFields"
      v-bind:key="field.key"
      v-bind:field="field"
      v-model="questionListData[field.key]"
      v-on:input="handleInput(field.key, $event)"/>
  </div>
</template>

<script>
import InputText from '@/components/inputs/simple/InputText.vue';
import InputNumber from '@/components/inputs/simple/InputNumber.vue';
import InputEmail from '@/components/inputs/simple/InputEmail.vue';
import InputPhone from '@/components/inputs/simple/InputPhone.vue';
import InputDate from '@/components/inputs/simple/InputDate.vue';
import InputBoolean from '@/components/inputs/simple/InputBoolean.vue';
import DropDown from '@/components/inputs/simple/DropDown.vue';
import RadioButtons from '@/components/inputs/simple/RadioButtons.vue';
import CheckBoxes from '@/components/inputs/simple/CheckBoxes.vue';
import ScrollBox from '@/components/inputs/simple/ScrollBox.vue';
import ScrollBoxRadio from '@/components/inputs/simple/ScrollBoxRadio.vue';
import ScrollBoxCheckbox from '@/components/inputs/simple/ScrollBoxCheckbox.vue';
import InputAddress from '@/components/inputs/complex/InputAddress.vue';
import InputFullName from '@/components/inputs/complex/InputFullName.vue';
import CmsText from '@/components/CmsText.vue';
import { mapState } from 'vuex';

/**
 * Question List
 * -- Implements v-model
 *
 * Iterates over a form field configuration array and creates a list of
 * form inputs components.
 */
export default {
  name: 'QuestionList',
  inject: ['$validator'],
  components: {
    InputText,
    InputNumber,
    InputEmail,
    InputPhone,
    InputDate,
    InputBoolean,
    DropDown,
    RadioButtons,
    CheckBoxes,
    ScrollBox,
    ScrollBoxRadio,
    ScrollBoxCheckbox,
    InputAddress,
    InputFullName,
    CmsText,
  },
  props: {
    /**
     * The array of form field configurations
     */
    formFields: Array,
    /**
     * The object to assign the field values to
     */
    value: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * The index of the question list in the form.
     */
    listIndex: {
      type: Number,
      default() {
        return 0;
      },
    },
    /**
     * Whether or not there is a non-field error.
     */
    isNonFieldError: Boolean,
  },
  data() {
    return {
      /**
       * The local store of the value prop (prevents race conditions)
       */
      questionListData: this.value,
    };
  },
  methods: {
    /**
     * Retrieves the input component for the given field type.
     * @param type {String} the field type
     * @return {string} the name of the matching component
     */
    getFieldComponent(type) {
      const capitalized = type.split('_').map(str => str.charAt(0).toUpperCase() + str.substr(1)).join('');
      switch (type) {
        case 'multiple_choice':
          return 'CheckBoxes';
        case 'dropdown':
          return 'DropDown';
        case 'radio':
          return 'RadioButtons';
        case 'scroll_box':
          return 'ScrollBox';
        case 'scroll_box_radio':
          return 'ScrollBoxRadio';
        case 'scroll_box_checkbox':
          return 'ScrollBoxCheckbox';
        default:
          return `Input${capitalized}`;
      }
    },
    /**
     * Handles input events raised by the components, updating the local value store
     * and emitting the value up to the parent.
     * @param key {String} the key to update in the value store
     * @param event {*} the value raised by the event
     */
    handleInput(key, event) {
      if (this.isNonFieldError) {
        this.$emit('clearNonFieldErrors');
      }
      this.questionListData[key] = event;
      this.$emit('input', this.questionListData);
    },
  },
  computed: {
    ...mapState('forms', ['verificationAttempts']),
    /**
     * Filters out time stamp fields which should never be rendered.
     *
     * @return {Array} Filtered form fields.
     */
    renderableFormFields() {
      return this.formFields?.filter(field => field.type !== 'time_stamp') || [];
    },
  },
  watch: {
    /**
     * Updates the local store with the new value prop.
     * @param newVal {Object} the new value for the value prop
     */
    value(newVal) {
      this.questionListData = newVal;
    },
  },
  mounted() {
    this.$nextTick().then(() => {
      if (this.formFields && this.listIndex === 0) {
        const topInput = this.$el.querySelector('input,textarea,select');
        topInput?.labels?.[0]?.focus();
      }
    });
  },
};
</script>
